import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Calendar as CalIcon, Clock, AlertTriangle, CheckCircle2,
  Wrench, ClipboardCheck, Users, Shield, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";

const typeConfig = {
  maintenance: { label: "Maintenance", icon: Wrench, color: "bg-blue-500" },
  corrective_action: { label: "Corrective Action", icon: AlertTriangle, color: "bg-orange-500" },
  audit: { label: "Audit", icon: ClipboardCheck, color: "bg-purple-500" },
  management_review: { label: "Review", icon: Users, color: "bg-teal-500" },
  risk_review: { label: "Risk Review", icon: Shield, color: "bg-red-500" }
};

const ComplianceCalendar = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/compliance-calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const filtered = events.filter(e => filterType === "all" || e.type === filterType);

  // Calendar grid helpers
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().split("T")[0];
  const monthStr = currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return filtered.filter(e => e.date === dateStr);
  };

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));
  const goToday = () => setCurrentMonth(new Date());

  // Upcoming events
  const upcoming = filtered.filter(e => e.date >= today).slice(0, 15);
  const overdue = filtered.filter(e => e.date < today);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="compliance-calendar-page">
      <PageHeader
        title={t("calendar.title")}
        description={t("calendar.subtitle")}
        actions={
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48" data-testid="calendar-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events ({events.length})</SelectItem>
              {Object.entries(typeConfig).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold">{events.length}</p>
          <p className="text-xs text-muted-foreground">Total Events</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold text-red-500">{overdue.length}</p>
          <p className="text-xs text-muted-foreground">Overdue</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold text-blue-500">{upcoming.length}</p>
          <p className="text-xs text-muted-foreground">Upcoming</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2">
          <Card className="border-border" data-testid="calendar-grid-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">{monthStr}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={goToday} className="text-xs">Today</Button>
                </div>
                <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                  <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
                ))}
              </div>
              {/* Calendar cells */}
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const dayEvents = getEventsForDay(day);
                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isToday = dateStr === today;
                  const isPast = dateStr < today;
                  return (
                    <div key={day} className={`min-h-[4rem] p-1 rounded-lg border transition-colors ${
                      isToday ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/30'
                    }`} data-testid={`cal-day-${day}`}>
                      <p className={`text-xs font-medium mb-0.5 ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>{day}</p>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map(ev => {
                          const cfg = typeConfig[ev.type] || typeConfig.maintenance;
                          return (
                            <div key={ev.id} className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer text-white ${isPast && ev.status !== 'completed' ? 'bg-red-500' : cfg.color}`}
                              onClick={() => navigate(ev.link)} title={ev.title}>
                              {ev.title.length > 20 ? ev.title.substring(0, 18) + ".." : ev.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && <p className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} more</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Events */}
        <div className="space-y-4">
          {overdue.length > 0 && (
            <Card className="border-red-500/30 bg-red-500/5" data-testid="overdue-events-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-red-500 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Overdue ({overdue.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {overdue.slice(0, 5).map(ev => {
                  const cfg = typeConfig[ev.type] || typeConfig.maintenance;
                  const Icon = cfg.icon;
                  return (
                    <div key={ev.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 p-2 rounded-lg" onClick={() => navigate(ev.link)}>
                      <Icon className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{ev.title}</p>
                        <p className="text-[10px] text-red-400">{ev.date}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <Card className="border-border" data-testid="upcoming-events-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CalIcon className="w-4 h-4" /> Upcoming
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No upcoming events</p>
              ) : (
                upcoming.map(ev => {
                  const cfg = typeConfig[ev.type] || typeConfig.maintenance;
                  const Icon = cfg.icon;
                  return (
                    <div key={ev.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 p-2 rounded-lg" onClick={() => navigate(ev.link)}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.color}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{ev.title}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-[10px] text-muted-foreground">{ev.date}</p>
                          <Badge variant="secondary" className="text-[10px] px-1 h-4">{cfg.label}</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ComplianceCalendar;
